import { useState } from 'react';
import { customColors } from '../../utils/analysisColorConfig';
import { assertCondition } from '../../../../utils/types';
import { Tab, TabList, Tabs } from '../../../Tabs';
import { IContextualMenuStyles } from '@fluentui/react';
import { InputField } from '../../../Form';
import * as ColorMenuUI from './ColorMenuUI';
interface IProps {
  currentColor: string;
  onSubmit: (selectedColor: string) => void;
}

// list and root were pre-defined, need to override them
export const colorMenuStyles: Partial<IContextualMenuStyles> = {
  list: {
    minWidth: '152px',
  },
  root: {
    minWidth: '152px',
  },
};

type ColorTab = 'Color' | 'Custom';

const TAB_LIST: ColorTab[] = ['Color', 'Custom'];

/**
 * Checks if the color is a valid hex color, it accepts both short and long hex codes
 * @param color - The color string to check
 * @returns true if the color is a valid hex color, false otherwise
 */
const isHexColor = (color: string) => {
  return /^#([0-9A-Fa-f]{3}){1,2}$/i.test(color);
};

const ColorMenu = ({ currentColor, onSubmit }: IProps) => {
  const [selectedTab, setSelectedTab] = useState<ColorTab>(
    customColors.includes(currentColor) ? 'Color' : 'Custom',
  );

  const [selectedCustomColor, setSelectedCustomColor] =
    useState<string>(currentColor);

  const handleSubmit = (color: string) => {
    assertCondition(isHexColor(color));
    onSubmit(color);
  };

  const onTabChange = (tabIndex: number) => {
    assertCondition(tabIndex >= 0 && tabIndex < TAB_LIST.length);
    setSelectedTab(TAB_LIST[tabIndex]);
  };

  const isCurrentCustomColorValid = isHexColor(selectedCustomColor);

  return (
    <ColorMenuUI.Container $hasPlaceholder={selectedTab === 'Color'}>
      <Tabs onTabChange={onTabChange}>
        <TabList>
          {TAB_LIST.map((tab, index) => {
            return (
              <Tab
                key={tab}
                tabIndex={index}
                defaultActive={tab === selectedTab}
              >
                {tab}
              </Tab>
            );
          })}
        </TabList>
      </Tabs>
      <ColorMenuUI.TabContentContainer>
        {selectedTab === 'Color' && (
          <ColorMenuUI.ColorGridContainer>
            {customColors.map((color) => (
              <ColorMenuUI.ColorGridItem
                key={color}
                onClick={() => handleSubmit(color)}
              >
                <ColorMenuUI.Circle
                  $color={color}
                  $isSelected={color === currentColor}
                />
              </ColorMenuUI.ColorGridItem>
            ))}
          </ColorMenuUI.ColorGridContainer>
        )}
        {selectedTab === 'Custom' && (
          <ColorMenuUI.CustomColorContainer>
            <InputField
              value={selectedCustomColor.toUpperCase()}
              hasError={!isCurrentCustomColorValid}
              onChange={(value) => {
                setSelectedCustomColor(value);
                if (isCurrentCustomColorValid) {
                  handleSubmit(value);
                }
              }}
            />
            <ColorMenuUI.StyledHexColorPicker
              color={selectedCustomColor}
              onChange={(color) => {
                setSelectedCustomColor(color);
                handleSubmit(color);
              }}
            />
          </ColorMenuUI.CustomColorContainer>
        )}
      </ColorMenuUI.TabContentContainer>
    </ColorMenuUI.Container>
  );
};

export default ColorMenu;
